/**
 * Six background-layer effects for the video studio. Each is a pure
 * draw-function called once per frame BEFORE the cover / halo / karaoke
 * overlay. Some carry small module-scoped state (particle positions,
 * bass ripple queue) which the caller must reset with `resetEffectState`
 * before starting a new render so a second video doesn't inherit the
 * first one's phase.
 *
 * Signature: (ctx, W, H, t, energy, cover) — `energy` is normalised
 * FFT-derived bass/mid/rms in 0..1. `cover` is the loaded image so
 * effects like kaleidoscope can sample real artwork when available.
 */

export type EffectEnergy = { bass: number; mid: number; treb: number; rms: number };

export type BackgroundFn = (
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  t: number,
  e: EffectEnergy,
  cover: HTMLImageElement | ImageBitmap | null,
) => void;

const TAU = Math.PI * 2;

// ── shared per-render state ──────────────────────────────────────────
const stars = Array.from({ length: 80 }, () => ({
  x: Math.random(), y: Math.random(),
  vx: (Math.random() - 0.5) * 0.02,
  vy: (Math.random() - 0.5) * 0.02,
  r: 0.4 + Math.random() * 1.6,
}));
const streaks = Array.from({ length: 120 }, () => ({
  angle: Math.random() * TAU,
  r: Math.random(),
  hue: 180 + Math.random() * 180,
  speed: 0.25 + Math.random() * 0.35,
}));
const bassRipples: { born: number }[] = [];
let lastBassSample = 0;

export function resetEffectState(): void {
  for (const s of stars) {
    s.x = Math.random(); s.y = Math.random();
    s.vx = (Math.random() - 0.5) * 0.02;
    s.vy = (Math.random() - 0.5) * 0.02;
  }
  for (const s of streaks) {
    s.angle = Math.random() * TAU;
    s.r = Math.random();
  }
  bassRipples.length = 0;
  lastBassSample = 0;
}

// ── 1. AURORA — flowing horizontal bands ───────────────────────────────
const auroraSeeds = [
  { hue: 155, speed: 0.15, amp: 0.06, phase: 0.0, y: 0.35, width: 0.28 },
  { hue: 179, speed: 0.20, amp: 0.08, phase: 1.3, y: 0.44, width: 0.34 },
  { hue: 203, speed: 0.25, amp: 0.10, phase: 2.6, y: 0.53, width: 0.40 },
  { hue: 227, speed: 0.30, amp: 0.12, phase: 3.9, y: 0.62, width: 0.46 },
  { hue: 251, speed: 0.35, amp: 0.14, phase: 5.2, y: 0.71, width: 0.52 },
];
export const drawAurora: BackgroundFn = (ctx, W, H, t, e) => {
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#040616');
  bg.addColorStop(1, '#01030a');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const s of auroraSeeds) {
    const yCenter = s.y * H + Math.sin(t * s.speed + s.phase) * H * s.amp * (0.7 + e.rms * 0.6);
    const band = ctx.createLinearGradient(0, yCenter - H * s.width * 0.5, 0, yCenter + H * s.width * 0.5);
    band.addColorStop(0, 'rgba(0,0,0,0)');
    band.addColorStop(0.5, `hsla(${s.hue}, 80%, 60%, ${0.35 + e.mid * 0.4})`);
    band.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = band;
    ctx.beginPath();
    for (let x = 0; x <= W; x += 8) {
      const wave = Math.sin(x * 0.012 + t * s.speed * 2 + s.phase) * H * 0.04 * (0.6 + e.mid * 0.6);
      const yTop = yCenter + wave - H * s.width * 0.5;
      if (x === 0) ctx.moveTo(x, yTop); else ctx.lineTo(x, yTop);
    }
    for (let x = W; x >= 0; x -= 8) {
      const wave = Math.sin(x * 0.012 + t * s.speed * 2 + s.phase) * H * 0.04 * (0.6 + e.mid * 0.6);
      const yBot = yCenter + wave + H * s.width * 0.5;
      ctx.lineTo(x, yBot);
    }
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
};

// ── 2. VAPORWAVE — retro synthwave sunset ──────────────────────────────
export const drawVaporwave: BackgroundFn = (ctx, W, H, t, e) => {
  const sky = ctx.createLinearGradient(0, 0, 0, H * 0.65);
  sky.addColorStop(0, '#2b0f4a');
  sky.addColorStop(0.55, '#802e6d');
  sky.addColorStop(1, '#e94a8f');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H * 0.65);
  const ground = ctx.createLinearGradient(0, H * 0.65, 0, H);
  ground.addColorStop(0, '#0a0316');
  ground.addColorStop(1, '#040108');
  ctx.fillStyle = ground;
  ctx.fillRect(0, H * 0.65, W, H * 0.35);
  // Sun.
  const sunR = Math.min(W, H) * (0.16 + e.bass * 0.04);
  const sunX = W / 2, sunY = H * 0.6;
  const sunG = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR);
  sunG.addColorStop(0, '#ffd280');
  sunG.addColorStop(0.5, '#ff6a6a');
  sunG.addColorStop(1, 'rgba(243, 74, 143, 0)');
  ctx.fillStyle = sunG;
  ctx.beginPath(); ctx.arc(sunX, sunY, sunR * 1.2, 0, TAU); ctx.fill();
  // Sun stripes.
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  for (let i = 1; i < 6; i++) {
    const y = sunY + i * sunR * 0.22 - sunR * 0.1;
    ctx.fillRect(sunX - sunR, y, sunR * 2, sunR * 0.08);
  }
  ctx.restore();
  // Perspective grid on ground.
  ctx.save();
  ctx.strokeStyle = 'rgba(233, 74, 143, 0.7)';
  ctx.lineWidth = 1.5;
  const horY = H * 0.65;
  const rows = 14;
  for (let i = 1; i <= rows; i++) {
    const pRaw = (i / rows + (t * 0.15)) % 1;
    const p = pRaw * pRaw;
    const y = horY + p * (H - horY);
    ctx.globalAlpha = 0.15 + p * 0.5;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }
  ctx.globalAlpha = 1;
  const cols = 16;
  const vpX = W / 2;
  for (let i = -cols / 2; i <= cols / 2; i++) {
    const x = vpX + i * (W / cols);
    ctx.beginPath();
    ctx.moveTo(x, H);
    ctx.lineTo(vpX, horY);
    ctx.stroke();
  }
  ctx.restore();
};

// ── 3. CONSTELLATION — drifting dots + lines ───────────────────────────
export const drawConstellation: BackgroundFn = (ctx, W, H, t, e) => {
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#050a17'); bg.addColorStop(1, '#020509');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  for (const s of stars) {
    s.x += s.vx * 0.002; s.y += s.vy * 0.002;
    if (s.x < 0) s.x = 1; if (s.x > 1) s.x = 0;
    if (s.y < 0) s.y = 1; if (s.y > 1) s.y = 0;
  }
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const maxDist = 0.16 + e.mid * 0.06;
  for (let i = 0; i < stars.length; i++) {
    for (let j = i + 1; j < stars.length; j++) {
      const a = stars[i], b = stars[j];
      const dx = a.x - b.x, dy = a.y - b.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < maxDist) {
        const p = 1 - d / maxDist;
        ctx.strokeStyle = `hsla(170, 70%, 60%, ${p * 0.5 + e.rms * 0.15})`;
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(a.x * W, a.y * H);
        ctx.lineTo(b.x * W, b.y * H);
        ctx.stroke();
      }
    }
  }
  for (const s of stars) {
    const r = s.r + e.bass * 1.4;
    ctx.fillStyle = `rgba(180, 240, 220, ${0.6 + e.bass * 0.3})`;
    ctx.beginPath();
    ctx.arc(s.x * W, s.y * H, r, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
  // t is passed for API consistency; not used here yet.
  void t;
};

// ── 4. RADIAL SPECTRUM — circular EQ + bass ripples ────────────────────
export const drawRadialSpectrum: BackgroundFn = (ctx, W, H, t, e) => {
  const bg = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.max(W, H) * 0.7);
  bg.addColorStop(0, `hsl(240, 40%, ${8 + e.bass * 10}%)`);
  bg.addColorStop(1, '#04030c');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  const cx = W / 2, cy = H / 2;
  const shortSide = Math.min(W, H);
  const coverSize = shortSide * 0.5;
  // Detect + spawn bass ripples.
  if (e.bass > 0.55 && e.bass - lastBassSample > 0.12) bassRipples.push({ born: t });
  lastBassSample = e.bass;
  while (bassRipples.length > 0 && t - bassRipples[0].born > 1.4) bassRipples.shift();
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const r of bassRipples) {
    const age = t - r.born;
    const p = age / 1.4;
    const radius = coverSize * 0.55 + p * shortSide * 0.55;
    const alpha = (1 - p) * 0.35;
    ctx.strokeStyle = `rgba(233, 74, 143, ${alpha})`;
    ctx.lineWidth = shortSide * 0.006 * (1 - p * 0.6);
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.stroke();
  }
  ctx.restore();
  const bars = 96;
  const inner = coverSize * 0.62;
  const outer = coverSize * (0.98 + e.bass * 0.35);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(t * 0.05);
  for (let i = 0; i < bars; i++) {
    const bin = Math.abs(Math.sin(i * 0.7 + t * 2)) * 0.7 + e.mid * 0.6 + e.treb * 0.3 * Math.abs(Math.sin(i));
    const angle = (i / bars) * TAU - Math.PI / 2;
    const r1 = inner;
    const r2 = inner + (outer - inner) * Math.min(1, bin);
    const hue = 160 + (i / bars) * 200;
    const grad = ctx.createLinearGradient(Math.cos(angle) * r1, Math.sin(angle) * r1, Math.cos(angle) * r2, Math.sin(angle) * r2);
    grad.addColorStop(0, `hsla(${hue % 360}, 85%, 60%, 0.9)`);
    grad.addColorStop(1, `hsla(${hue % 360}, 85%, 70%, 0)`);
    ctx.strokeStyle = grad;
    ctx.lineWidth = Math.max(1.5, TAU * inner / bars * 0.55);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(Math.cos(angle) * r1, Math.sin(angle) * r1);
    ctx.lineTo(Math.cos(angle) * r2, Math.sin(angle) * r2);
    ctx.stroke();
  }
  ctx.restore();
};

// ── 5. NEON TUNNEL — light streaks radiating outward ───────────────────
export const drawTunnel: BackgroundFn = (ctx, W, H, t, e) => {
  // Trail fade — do not fully clear so streaks smear.
  ctx.fillStyle = 'rgba(3, 5, 12, 0.35)';
  ctx.fillRect(0, 0, W, H);
  const cx = W / 2, cy = H / 2;
  const maxR = Math.max(W, H) * 0.75;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const s of streaks) {
    s.r += s.speed * 0.012 * (0.7 + e.bass * 1.6);
    if (s.r > 1) { s.r = 0; s.angle = Math.random() * TAU; }
    const p = s.r;
    const r1 = maxR * p;
    const r2 = maxR * Math.max(0, p - 0.06);
    const x1 = cx + Math.cos(s.angle) * r1;
    const y1 = cy + Math.sin(s.angle) * r1;
    const x2 = cx + Math.cos(s.angle) * r2;
    const y2 = cy + Math.sin(s.angle) * r2;
    const grad = ctx.createLinearGradient(x1, y1, x2, y2);
    grad.addColorStop(0, `hsla(${s.hue}, 90%, 70%, ${0.9 * p})`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.strokeStyle = grad;
    ctx.lineWidth = 1.5 + p * 2.5;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.restore();
  void t;
};

// ── 6. KALEIDOSCOPE — mirrored wedges of the cover (or a palette) ──────
let kaleidoSource: HTMLCanvasElement | null = null;
export const drawKaleidoscope: BackgroundFn = (ctx, W, H, t, e, cover) => {
  ctx.fillStyle = '#04030a';
  ctx.fillRect(0, 0, W, H);
  const cx = W / 2, cy = H / 2;
  const wedges = 8;
  const radius = Math.max(W, H) * 0.62;
  // Build the sampled source. If we have the real cover, use it; else
  // synthesize a colour cell so the wedges still read as varied.
  if (!kaleidoSource) {
    kaleidoSource = document.createElement('canvas');
    kaleidoSource.width = 240;
    kaleidoSource.height = 240;
  }
  const sctx = kaleidoSource.getContext('2d')!;
  sctx.clearRect(0, 0, 240, 240);
  if (cover) {
    // Sample a moving 240×240 window of the cover so the wedges look
    // like they're panning across the artwork.
    const iw = (cover as any).naturalWidth || (cover as any).width || 240;
    const ih = (cover as any).naturalHeight || (cover as any).height || 240;
    const size = Math.min(iw, ih);
    const cxSrc = iw / 2 + Math.cos(t * 0.4) * size * 0.15;
    const cySrc = ih / 2 + Math.sin(t * 0.5) * size * 0.15;
    sctx.drawImage(cover, cxSrc - size / 2, cySrc - size / 2, size, size, 0, 0, 240, 240);
    // Tint with a shifting hue overlay so the wedges shift color with time.
    sctx.globalCompositeOperation = 'overlay';
    const hue = (t * 30) % 360;
    sctx.fillStyle = `hsla(${hue}, 70%, 50%, 0.35)`;
    sctx.fillRect(0, 0, 240, 240);
    sctx.globalCompositeOperation = 'source-over';
  } else {
    const hue = (200 + t * 30) % 360;
    const g = sctx.createLinearGradient(0, 0, 240, 240);
    g.addColorStop(0, `hsl(${hue}, 75%, 55%)`);
    g.addColorStop(1, `hsl(${(hue + 60) % 360}, 75%, 35%)`);
    sctx.fillStyle = g;
    sctx.fillRect(0, 0, 240, 240);
    for (let i = 0; i < 5; i++) {
      const dx = 120 + Math.cos(t * 0.9 + i) * 80;
      const dy = 120 + Math.sin(t * 0.7 + i * 1.7) * 80;
      const r = 36 + Math.sin(t * 2 + i) * 16;
      sctx.fillStyle = `hsla(${(hue + i * 50) % 360}, 90%, 70%, 0.7)`;
      sctx.beginPath(); sctx.arc(dx, dy, r, 0, TAU); sctx.fill();
    }
  }
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(t * 0.15 + e.bass * 0.2);
  for (let i = 0; i < wedges; i++) {
    ctx.save();
    ctx.rotate((i / wedges) * TAU);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, radius, -Math.PI / wedges, Math.PI / wedges);
    ctx.closePath();
    ctx.clip();
    if (i % 2 === 1) ctx.scale(-1, 1);
    ctx.drawImage(kaleidoSource, -radius, -radius, radius * 2, radius * 2);
    ctx.restore();
  }
  ctx.restore();
  // Radial fade so the frame edge stays dark and the cover stands out.
  const fade = ctx.createRadialGradient(cx, cy, radius * 0.65, cx, cy, radius);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, W, H);
};

// ── Registry + picker ──────────────────────────────────────────────────
export const VIDEO_STYLES = [
  { key: 'aurora', label: 'Aurora waves', draw: drawAurora },
  { key: 'vaporwave', label: 'Vaporwave sunset', draw: drawVaporwave },
  { key: 'constellation', label: 'Constellation', draw: drawConstellation },
  { key: 'radial', label: 'Radial spectrum', draw: drawRadialSpectrum },
  { key: 'tunnel', label: 'Neon tunnel', draw: drawTunnel },
  { key: 'kaleidoscope', label: 'Kaleidoscope', draw: drawKaleidoscope },
] as const;

export type VideoStyleKey = typeof VIDEO_STYLES[number]['key'];
export type VideoStyleChoice = VideoStyleKey | 'random';

export function pickBackground(choice: VideoStyleChoice): { key: VideoStyleKey; draw: BackgroundFn; label: string } {
  if (choice === 'random') {
    const idx = Math.floor(Math.random() * VIDEO_STYLES.length);
    return VIDEO_STYLES[idx];
  }
  return VIDEO_STYLES.find((s) => s.key === choice) || VIDEO_STYLES[0];
}
